import React from 'react';
import PropTypes from 'prop-types';
import { ApolloProvider } from '@apollo/client';
import { QueryClientProvider, QueryClient } from 'react-query';

// Components
import SearchPage from './SearchPage';
import SearchAuthCheck from '../../sections/SearchAuthCheck';
import ErrorHandler from '../../sections/ErrorHandler';

// Functionality
import { ViewTypeContext } from '@context/viewTypeContext';
import { SearchTypeContext } from '../../../context/searchTypeContext';
import {
    SortByContext,
    PageSizeContext,
    SearchModeContext,
    ConfigFilterContext,
    AttendeeModeContext,
} from '../../../context/searchContext';

import useSearchType from '../../../hooks/useSearchType';
import useSortBy from '../../../hooks/useSortBy';
import usePageSize from '../../../hooks/usePageSize';
import useConfigFilter from '../../../hooks/useConfigFilter';
import useSearchMode from '../../../hooks/useSearchMode';
import useAttendeeMode from '../../../hooks/useAttendeeMode';
import LoggedInProvider from '../../../../../icsc-app/js/context/LoggedInProvider';
import client from '@general/graphql/client';
import useViewType from '@hooks/useViewType';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // for dev - allows for errors to resolve instead of retrying
            retry: false,
            // TODO: Test this out - https://codemachine.dev/things-i-learned-while-using-react-query-part-2
            // retry: process.env.NODE_ENV === 'production',
            // refetchOnWindowFocus: process.env.NODE_ENV === 'production',
        },
    },
});

/**
 * @param {Object} props
 * @param {object} props.event - attendee search /attend-and-learn/events/attendees/recon-canada
 * @param {string} props.event.eventCode - attendee search - for filter string
 * @param {string} props.event.eventName - attendee search - for display
 * @param {string} props.event.eventDate - attendee search - for display
 * @param {number} props.companyId - member search for company /company/profile/765163
 * @param {string} props.isPublicationsSearch - publication search in everything search /news-and-views/newsstand
 */
const AlgoliaSearch = ({
    children,
    event = {},
    companyId,
    isPublicationsSearch = false,
    defaultSearchType = null,
    showFilters = true,
}) => {
    const { eventCode } = event;

    const isMemberCompanySearch = !!companyId;

    // the order matters!
    // store attendee search data first - includes event data: title, date, etc.
    const { isAttendeeSearch, ...restAttendeeProps } = useAttendeeMode(event);

    // set search type depending on props and if attendee search
    const { searchType, setSearchType } = useSearchType({
        isAttendeeSearch,
        isMemberCompanySearch,
        isPublicationsSearch,
        defaultSearchType,
    });

    // set various helpful triggers depending on search type and all the options
    const { ...restSearchModeProps } = useSearchMode({
        isPublicationsSearch,
        searchTypeIndex: searchType.index,
        isAttendeeSearch,
        isMemberCompanySearch,
        eventCode,
        showFilters,
    });

    const { viewType, setViewType } = useViewType({
        defaultView: isPublicationsSearch || defaultSearchType?.index === 'properties' ? 'card' : null,
    });
    const { sortBy, setSortBy, setSortByDefault } = useSortBy(searchType);
    const { pageSize, setPageSize } = usePageSize();

    // set filter string for algolia based on all of the above
    const { ...restConfigFilter } = useConfigFilter({
        eventCode,
        initialShowPastItems: searchType.showPastItems,
        companyId,
        isPublicationsSearch,
        searchType,
    });

    return (
        <ErrorHandler>
            <ApolloProvider client={client}>
                <QueryClientProvider client={queryClient}>
                    <LoggedInProvider>
                        <AttendeeModeContext.Provider value={{ isAttendeeSearch, ...restAttendeeProps }}>
                            <SearchModeContext.Provider
                                value={{
                                    isAttendeeSearch,
                                    isMemberCompanySearch,
                                    isPublicationsSearch,
                                    ...restSearchModeProps,
                                }}
                            >
                                <SearchTypeContext.Provider value={[searchType, setSearchType]}>
                                    <ViewTypeContext.Provider value={[viewType, setViewType]}>
                                        <SortByContext.Provider value={[sortBy, setSortBy, setSortByDefault]}>
                                            <PageSizeContext.Provider value={[pageSize, setPageSize]}>
                                                <ConfigFilterContext.Provider value={restConfigFilter}>
                                                    <SearchAuthCheck
                                                        isAttendeeSearch={isAttendeeSearch}
                                                        eventCode={event?.eventCode}
                                                    >
                                                        {children || <SearchPage />}
                                                    </SearchAuthCheck>
                                                </ConfigFilterContext.Provider>
                                            </PageSizeContext.Provider>
                                        </SortByContext.Provider>
                                    </ViewTypeContext.Provider>
                                </SearchTypeContext.Provider>
                            </SearchModeContext.Provider>
                        </AttendeeModeContext.Provider>
                    </LoggedInProvider>
                </QueryClientProvider>
            </ApolloProvider>
        </ErrorHandler>
    );
};

AlgoliaSearch.propTypes = {
    children: PropTypes.node,
    event: PropTypes.shape({
        eventCode: PropTypes.string,
        eventName: PropTypes.string,
        eventDate: PropTypes.string,
    }),
    companyId: PropTypes.number,
    isPublicationsSearch: PropTypes.bool,
    defaultSearchType: PropTypes.object,
    showFilters: PropTypes.bool,
};

export default AlgoliaSearch;
